import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Plus, Trash2, Eye, EyeOff, Edit2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    image_url: '',
    links: []
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/admin/news`, getAuthHeaders());
      setNews(response.data);
    } catch (error) {
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        links: formData.links.filter(l => l.url && l.label)
      };
      
      if (editingId) {
        await axios.put(`${API}/news/${editingId}`, data, getAuthHeaders());
        toast.success('News updated!');
      } else {
        await axios.post(`${API}/news`, data, getAuthHeaders());
        toast.success('News added!');
      }
      resetForm();
      fetchNews();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: '', subtitle: '', description: '', image_url: '', links: [] });
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      subtitle: item.subtitle,
      description: item.description,
      image_url: item.image_url,
      links: item.links || []
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleTogglePublish = async (id) => {
    try {
      const response = await axios.patch(`${API}/news/${id}/publish`, {}, getAuthHeaders());
      toast.success(response.data.is_published ? 'News published' : 'News unpublished');
      fetchNews();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news item?')) return;
    try {
      await axios.delete(`${API}/news/${id}`, getAuthHeaders());
      toast.success('News deleted');
      fetchNews();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const addLink = () => {
    setFormData(prev => ({
      ...prev,
      links: [...prev.links, { label: '', url: '', icon: 'link' }]
    }));
  };

  const updateLink = (index, field, value) => {
    const newLinks = [...formData.links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setFormData(prev => ({ ...prev, links: newLinks }));
  };

  const removeLink = (index) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="admin-news">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif">News</h1>
          <p className="text-muted-foreground">Manage news and announcements</p>
        </div>
        <Button onClick={() => { setShowForm(true); setEditingId(null); }}>
          <Plus className="w-4 h-4 mr-2" /> Add News
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg border border-border p-6 mb-6">
          <h2 className="font-semibold mb-4">{editingId ? 'Edit News' : 'New News Item'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subtitle</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none resize-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Image URL</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
                placeholder="https://..."
                required
              />
              {formData.image_url && (
                <img src={formData.image_url} alt="Preview" className="mt-2 max-w-xs rounded border" />
              )}
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Links (optional)</label>
                <Button type="button" variant="outline" size="sm" onClick={addLink}>
                  <Plus className="w-3 h-3 mr-1" /> Add Link
                </Button>
              </div>
              {formData.links.map((link, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={link.label}
                    onChange={(e) => updateLink(index, 'label', e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                    placeholder="Link text"
                  />
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => updateLink(index, 'url', e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                    placeholder="https://..."
                  />
                  <select
                    value={link.icon}
                    onChange={(e) => updateLink(index, 'icon', e.target.value)}
                    className="px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                  >
                    <option value="link">Link</option>
                    <option value="spotify">Spotify</option>
                    <option value="youtube">YouTube</option>
                  </select>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeLink(index)} className="text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <Button type="submit">{editingId ? 'Update' : 'Add'} News</Button>
              <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {news.length === 0 ? (
          <p className="col-span-2 text-center text-muted-foreground py-8">No news items yet</p>
        ) : (
          news.map((item) => (
            <div key={item.id} className="bg-white rounded-lg border border-border overflow-hidden">
              {item.image_url && (
                <img src={item.image_url} alt={item.title} className="w-full h-40 object-cover" />
              )}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-xs text-accent-gold uppercase">{item.subtitle}</span>
                    <h3 className="font-semibold">{item.title}</h3>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${item.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {item.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{item.description}</p>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleTogglePublish(item.id)}>
                    {item.is_published ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                    {item.is_published ? 'Unpublish' : 'Publish'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                    <Edit2 className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminNews;
